import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core"
import { MatDialog } from "@angular/material/dialog"
import { DaoBase } from "../daos/dao-base"
import { TrColumnFilterMap, TrDateFilter, TrFilterDialogData, TrFilterType } from "../models/models"
import { Subscription } from "rxjs"
import { TrFilterDialogComponent } from "../tr-filter-dialog/tr-filter-dialog.component"

@Component({
  selector: "tr-filter-header",
  styleUrls: ["tr-filter-header.component.scss"],
  templateUrl: "tr-filter-header.component.html",
})
export class TrFilterHeaderComponent<T> implements OnInit, OnDestroy {
  @Input() public dao: DaoBase<T>
  @Input() public filterColumn: string
  @Input() public filterTitle: string
  @Input() public filterType: TrFilterType = "input"
  @Input() public useOptionsFromData = false
  @Input() public selectOptions?: string[]
  @Input() public selectMultiple = false
  @Input() public enableSelectAll = true
  @Input() public enableSelectAny = true
  @Input() public hideAllAnyOption = false
  @Input() public checkboxLabel?: string
  @Input() public checkboxTrueValue = "true"
  @Input() public checkboxFalseValue = "false"
  @Input() public dateConfig?: TrDateFilter

  @Output() public filterDefaultsChange = new EventEmitter<TrColumnFilterMap>()

  private totalDataSubscription: Subscription
  constructor(public dialog: MatDialog) {}

  public ngOnInit(): void {
    if (!this.filterColumn) {
      throw new Error(`tr-filter-header must include the input 'name' to define which column to filter by. (filterColumn=${this.filterColumn})`)
    }

    if (!this.filterType) {
      throw new Error(`tr-filter-header must include the input 'filterType' to define which column to filter by. (filterColumn=${this.filterColumn})`)
    }

    if (this.filterType === "dropdown") {
      if (!this.selectOptions && !this.useOptionsFromData) {
        throw new Error(`tr-filter-header with filterType=dropdown must include 'selectOptions' through an input. (filterColumn=${this.filterColumn})`)
      }

      this.totalDataSubscription = this.dao.totalData
        .subscribe(() => this.setSelectedOptions())
    }

    const filter = this.dao.filterDefaults[this.filterColumn]
    if (filter) {
      filter.type = this.filterType
    }
  }

  public ngOnDestroy(): void {
    if (this.totalDataSubscription) {
      this.totalDataSubscription.unsubscribe()
    }
  }

  public isActiveFilter(): boolean {
    return this.dao.isActiveFilter(this.filterColumn)
  }

  public convertArrayStrings(item: any): void {
    return item[this.filterColumn]
  }

  public setSelectedOptions(): void {
    if (this.useOptionsFromData) {
      const nonUniqueSelectOptions = []
      this.dao.totalData.value
        .map(item => this.dao.convertValuesToString(item, this.filterColumn))
        .filter(item => !!item)
        .forEach(item => {
          nonUniqueSelectOptions.push(...item.split(", "))
        })

      this.selectOptions = [...new Set(nonUniqueSelectOptions)]
    }
  }

  public async openDialog(event: MouseEvent): Promise<void> {
    this.setSelectedOptions()

    event.stopImmediatePropagation()

    const dialogRef = this.dialog.open(TrFilterDialogComponent, {
      width: this.getDialogWidth(),
      data: {
        filterType: this.filterType,
        defaultValue: this.dao.filterDefaults[this.filterColumn] ? this.dao.filterDefaults[this.filterColumn].data : undefined,
        selectOptions: this.selectOptions,
        selectMultiple: this.selectMultiple,
        enableSelectAll: this.enableSelectAll,
        enableSelectAny: this.enableSelectAny,
        hideAllAnyOption: this.hideAllAnyOption,
        checkboxTrueValue: this.checkboxTrueValue,
        checkboxFalseValue: this.checkboxFalseValue,
        placeholder: this.filterType === "dropdown" ? "Includes" : "Contains",
        title: this.filterTitle,
      } as TrFilterDialogData,
    })

    const result = await dialogRef.afterClosed().toPromise()
    if (result === null || result === undefined) {
      delete this.dao.filterDefaults[this.filterColumn]
    } else {
      this.dao.filterDefaults[this.filterColumn] = {
        data: result,
        type: this.filterType,
      }
    }

    this.dao.updateDataFromFilter()
  }

  public getDialogWidth(): string {
    switch (this.filterType) {
    case "date":
      return "320px"
    case "dropdown":
      return "400px"
    default:
      return "250px"
    }
  }
}
